import type { Eip1193Provider } from "./eip1193";
import { checkElysiumContracts, confirmElysiumHead, readElysiumHead, type ElysiumObservation } from "./elysium-readiness";
import type { RpcReadOptions } from "./read-rpc";

/** Display only, owned by one wallet/deployment context. Never used by writes. */
export class NftDisplayCache {
  private verified: { head: ElysiumObservation; expires: number } | null = null;
  private snapshot = "";
  private reads = new Map<string, { expires: number; promise: Promise<unknown>; signal?: AbortSignal }>();

  clear() { this.verified = null; this.snapshot = ""; this.reads.clear(); }

  provider(source: Eip1193Provider, signal: AbortSignal): Eip1193Provider {
    return { request: args => {
      signal.throwIfAborted();
      const params = args.params;
      // Reuse only exact calls at the currently verified block hash. In particular,
      // latest heads and canonical-header checks always reach the network.
      const pinned = Array.isArray(params) && typeof params[1] === "string" && /^0x[0-9a-f]+$/i.test(params[1]);
      if (!this.snapshot || !pinned || !["eth_call", "eth_getCode"].includes(args.method)) return source.request(args);
      const key = JSON.stringify([this.snapshot, args.method, params]);
      const entry = this.reads.get(key);
      if (entry && !entry.signal?.aborted && performance.now() < entry.expires) return entry.promise;
      const next: { expires: number; signal?: AbortSignal; promise: Promise<unknown> } = {
        expires: performance.now() + 30_000, signal, promise: source.request(args) };
      if (this.reads.size >= 128) this.reads.clear();
      this.reads.set(key, next);
      next.promise.then(() => {
        // A completed response no longer belongs to its caller's cancellation.
        if (this.reads.get(key) === next && !signal.aborted) delete next.signal;
      }, () => { if (this.reads.get(key) === next) this.reads.delete(key); });
      return next.promise;
    } };
  }

  async observe(provider: Eip1193Provider, guard: () => void, options: RpcReadOptions): Promise<ElysiumObservation> {
    try {
      const head = await readElysiumHead(provider, guard, options);
      const identity = `${head.blockNumber}:${head.blockHash}`;
      if (identity !== this.snapshot) { this.reads.clear(); this.snapshot = identity; }
      const cached = this.verified;
      if (cached && performance.now() < cached.expires && head.blockNumber >= cached.head.blockNumber) {
        // Even a cache hit must reject a reorg of the verification snapshot.
        await confirmElysiumHead(provider, cached.head, guard, options);
      } else {
        await checkElysiumContracts(provider, head, guard, options);
        await confirmElysiumHead(provider, head, guard, options);
        guard();
        this.verified = { head, expires: performance.now() + 30_000 };
      }
      return head;
    } catch (error) {
      // Closing one selection must not evict a newer selection's verification.
      if (!options.signal?.aborted) this.clear();
      throw error;
    }
  }
}
